/**
 * Story 052 — Preferences section.
 *
 * Theme + timezone. Theme persists in localStorage via @aeriosiq/ui's
 * `useColorScheme`; timezone read-only display in MVP (BE persist path
 * lives behind Zitadel metadata — wired in a follow-up).
 */
import { useContext, type ReactElement } from "react";
import { MenuItem, Stack, Typography } from "@mui/material";
// SB6-AUTH-CONSISTENCY-01 — funnel the Theme picker through AccountField.
import { AccountField } from "@aeriosiq/ui";
import { SectionShell } from "./SectionShell.js";
import { ColorSchemeContext } from "../../../app/providers.js";

export function PreferencesSection(): ReactElement {
  const colorScheme = useContext(ColorSchemeContext);
  const current = colorScheme?.isExplicit ? colorScheme.scheme : "system";

  // Try to read browser timezone for default — readonly display.
  let tz = "Europe/Berlin";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || tz;
  } catch {
    // ignore
  }

  return (
    <SectionShell id="preferences" title="Preferences">
      <Stack spacing={3}>
        <AccountField
          select
          label="Theme"
          value={current}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "system") {
              colorScheme?.resetToSystem();
              return;
            }
            if (next === "light" || next === "dark") {
              colorScheme?.setScheme(next);
            }
          }}
          sx={{ maxWidth: 280 }}
          fullWidth={false}
        >
          <MenuItem value="light">Light</MenuItem>
          <MenuItem value="dark">Dark</MenuItem>
          <MenuItem value="system">System</MenuItem>
        </AccountField>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Timezone
          </Typography>
          <Typography variant="body1">{tz}</Typography>
          <Typography variant="caption" color="text.secondary">
            Detected from your browser. Override coming in a future release.
          </Typography>
        </Stack>
      </Stack>
    </SectionShell>
  );
}
